import { type ReactNode, useCallback, useId, useState } from 'react'

import { ICONS } from '@/components/icons/icons'

import styles from './HowItWorks.module.css'

interface HowItWorksProps {
  /** localStorage key remembering whether this reader collapsed the panel. */
  storageKey: string
  /** What Sage drives with no action from anyone. */
  automatic: ReactNode
  /** What this page changes. */
  here: ReactNode
  /** Who may change it, phrased for the signed-in user. */
  who: ReactNode
}

function readCollapsed(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === 'collapsed'
  } catch {
    return false
  }
}

function writeCollapsed(key: string, collapsed: boolean): void {
  try {
    if (collapsed) window.localStorage.setItem(key, 'collapsed')
    else window.localStorage.removeItem(key)
  } catch {
    // Storage blocked (private mode): the panel just opens again next time.
  }
}

/** A short, collapsible "How this works" strip: automatic from Sage / you change here / who can. */
export function HowItWorks({ storageKey, automatic, here, who }: HowItWorksProps) {
  const [collapsed, setCollapsed] = useState(() => readCollapsed(storageKey))
  const bodyId = useId()
  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      writeCollapsed(storageKey, !prev)
      return !prev
    })
  }, [storageKey])

  return (
    <section className={styles.panel} aria-label="How this works">
      <button
        type="button"
        className={styles.toggle}
        onClick={toggle}
        aria-expanded={!collapsed}
        aria-controls={bodyId}
      >
        <ICONS.info size={14} className={styles.icon} />
        <span className={styles.heading}>How this works</span>
        <span className={styles.hint}>{collapsed ? 'Show' : 'Hide'}</span>
      </button>
      {collapsed ? null : (
        <dl className={styles.grid} id={bodyId}>
          <div className={styles.item}>
            <dt>Automatic from Sage</dt>
            <dd>{automatic}</dd>
          </div>
          <div className={styles.item}>
            <dt>What you change here</dt>
            <dd>{here}</dd>
          </div>
          <div className={styles.item}>
            <dt>Who can change it</dt>
            <dd>{who}</dd>
          </div>
        </dl>
      )}
    </section>
  )
}
